import { useState } from 'react'
import { ErrorMessage } from '../../../shared/ui/ErrorMessage'
import { AutoTextarea } from '../../../shared/ui/AutoTextarea'
import type { SourceType } from '../../../shared/api/types'
import { useMatrixCellQuotes } from '../hooks/useMatrixCellQuotes'
import type { MatrixRates } from '../utils/convertQuoteToMatrixField'
import { SupplierDuplicateHint } from '../../suppliers/components/SupplierDuplicateHint'

export interface CellQuoteTarget {
  itemId: string
  sourceType: SourceType
  matrixLabel: string
  /** Цена, введённая прямо в ячейку (строка) — предзаполняет поле. */
  prefillPrice: string
}

/** Часто используемые поставщики — быстрый выбор из списка. */
const SUPPLIER_PRESETS = ['ДКО (Компел)', 'Промэлектроника', 'Элитан', 'ЧИП И ДИП'] as const
/** Спец-значение: ввести произвольный источник (ссылку), которая уйдёт в заметку. */
const OTHER_SUPPLIER = 'Другой поставщик'
/** Дефолтный поставщик матрицы «Склад» — свой склад; можно стереть и ввести другого. */
const STOCK_DEFAULT_SUPPLIER = 'Склад СПБ'

/**
 * Модалка создания квоты из ячейки матрицы. Открывается, когда менеджер
 * вводит цену в ячейку (цена прилетает предзаполненной) или жмёт «+ Добавить
 * квоту» в поповере-списке (цена пустая). Матрица зафиксирована — это та
 * колонка, в которой шёл ввод; для РФ валюта принудительно рублёвая.
 *
 * После сохранения квота создаётся обычным createProcurementResult, а поле
 * ячейки получает «последнюю внесённую цену» (см. useMatrixCellQuotes).
 */
export function CellQuoteModal({
  target,
  requestId,
  rates,
  onClose,
}: {
  target: CellQuoteTarget
  requestId: string | undefined
  rates: MatrixRates | null | undefined
  onClose: () => void
}) {
  // ratesReady === false — курсы не загружены: цену матрицы считать не по чему,
  // квоту не создаём вовсе (иначе в позицию уедет цена по выдуманному курсу).
  const { createQuote, ratesReady } = useMatrixCellQuotes(requestId, rates)
  const isRF = target.sourceType === 'RF'
  const isStock = target.sourceType === 'STOCK'
  // Свободный ввод названия — для всех матриц, кроме РФ. Для «Склада»
  // предзаполняем свой склад — его можно стереть и ввести другого поставщика.
  const [supplierName, setSupplierName] = useState(isStock ? STOCK_DEFAULT_SUPPLIER : '')
  // Выбор из списка — только для матрицы РФ.
  const [supplierChoice, setSupplierChoice] = useState('')
  const [otherSource, setOtherSource] = useState('')
  const [price, setPrice] = useState(target.prefillPrice)
  // РФ — всегда рубли (селект заблокирован); Склад — по умолчанию рубли,
  // но валюту можно сменить; остальные матрицы — юани.
  const [currency, setCurrency] = useState(isRF || isStock ? 'RUB' : 'CNY')
  const [note, setNote] = useState('')
  const [err, setErr] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)

  const isOther = isRF && supplierChoice === OTHER_SUPPLIER
  const supplierValid = !isRF
    ? supplierName.trim().length > 0
    : isOther
      ? otherSource.trim().length > 0
      : supplierChoice.trim().length > 0
  const canSave = supplierValid && parseFloat(price) > 0 && !saving && ratesReady

  const save = async () => {
    if (!canSave) return
    setSaving(true)
    setErr(null)
    try {
      // РФ: выбор из списка, «Другой поставщик» → имя обобщённое + источник в заметку.
      // Остальные матрицы: свободный ввод названия, как раньше.
      const finalSupplierName = !isRF
        ? supplierName.trim()
        : isOther
          ? OTHER_SUPPLIER
          : supplierChoice.trim()
      const finalNote = isOther
        ? [`Откуда покупаем: ${otherSource.trim()}`, note.trim()].filter(Boolean).join('\n')
        : note.trim()
      await createQuote(target.itemId, target.sourceType, {
        supplierName: finalSupplierName,
        price: parseFloat(price),
        currency,
        note: finalNote || undefined,
      })
      onClose()
    } catch (e) {
      setErr(e)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !saving && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            Новая квота · матрица «{target.matrixLabel}»
          </h3>
          <button
            onClick={() => !saving && onClose()}
            className="text-gray-400 hover:text-gray-700 text-lg leading-none"
            title="Закрыть"
          >
            ×
          </button>
        </div>

        {!ratesReady && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            Курсы валют не загрузились — квоту сохранить нельзя: цену позиции считать не по чему.
          </div>
        )}

        {err != null && <ErrorMessage error={err} />}

        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Поставщик *</label>
          {isRF ? (
            <>
              <select
                autoFocus
                value={supplierChoice}
                onChange={(e) => setSupplierChoice(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="">Выберите поставщика…</option>
                {SUPPLIER_PRESETS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
                <option value={OTHER_SUPPLIER}>{OTHER_SUPPLIER}…</option>
              </select>
              {isOther && (
                <input
                  value={otherSource}
                  onChange={(e) => setOtherSource(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void save()}
                  className="mt-1.5 w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
                  placeholder="Ссылка / откуда покупаем (добавится в заметку)"
                />
              )}
            </>
          ) : (
            <input
              autoFocus
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void save()}
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Название компании"
            />
          )}
          {!isRF && <SupplierDuplicateHint name={supplierName} onPick={setSupplierName} />}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Цена за ед. *</label>
            <div className="flex gap-1">
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void save()}
                placeholder="0.00"
                className="flex-1 w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                disabled={isRF}
                title={isRF ? 'В матрице РФ цены всегда в рублях' : undefined}
                className="w-16 px-1 py-1.5 border border-gray-300 rounded text-xs bg-white focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option>USD</option>
                <option>EUR</option>
                <option>CNY</option>
                <option>RUB</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Матрица</label>
            <input
              value={target.matrixLabel}
              disabled
              className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs bg-gray-100 text-gray-500"
            />
          </div>
        </div>

        <div>
          <label className="flex items-center justify-between text-xs text-gray-500 mb-0.5">
            <span>Заметка</span>
            <span className="text-[10px] text-gray-400">{note.length}/500</span>
          </label>
          <AutoTextarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 500))}
            maxLength={500}
            minRows={2}
            className="w-full min-h-[2.25rem] px-2 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-brand-500"
            placeholder="Срок поставки, условия, ограничения поставщика…"
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={() => !saving && onClose()}
            className="px-3 py-1.5 text-xs text-gray-600 rounded-lg border border-gray-200 hover:bg-gray-50"
          >
            Отмена
          </button>
          <button
            onClick={() => void save()}
            disabled={!canSave}
            className="px-3 py-1.5 bg-brand-600 text-white text-xs rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {saving ? '…' : 'Добавить квоту'}
          </button>
        </div>
      </div>
    </div>
  )
}
